// WATCHTOWER design tokens — ported verbatim from the MT5-Fleet-Console mockup.
// IBM Plex dark theme. Color alone is never the only signal (every status is icon+text+color).

export const MONO = "'IBM Plex Mono',ui-monospace,monospace";
export const SANS = "'IBM Plex Sans',system-ui,sans-serif";

export const C = {
  bg: '#0C121D',
  s1: '#101828', // cards & tables
  s2: '#16223A', // hover & wells
  ln: '#22304A', // borders & dividers
  ln2: '#1B2740',
  tx: '#E6ECF6', // primary text
  dm: '#8598B3', // secondary text
  bl: '#4C9EE8', // info · selection · long
  gn: '#3FBF7F', // healthy · profit
  gn2: '#2F8F5F',
  am: '#E0A83E', // warning · delayed
  rd: '#E45B5B', // critical · offline · loss
  gy: '#78849B', // stale · unknown
  violet: '#B392D9', // short direction
  dim2: '#5A6A85',
  b9: '#B9C6DA',
} as const;

/** rgba() from a hex token at a given alpha — mirrors the mockup `hexA`. */
export function hexA(h: string, al: number): string {
  const n = parseInt(h.slice(1), 16);
  return `rgba(${n >> 16},${(n >> 8) & 255},${n & 255},${al})`;
}

// ---- State-machine tokens: [glyph, color, label] --------------------------------------
// Match the enums in contracts/dashboard-api.md and the mockup CM/BM/RM/TM/QM tables.

export type StateToken = readonly [glyph: string, color: string, label: string];

export const CM: Record<string, StateToken> = {
  live: ['●', C.gn, 'Live'],
  delayed: ['◐', C.am, 'Delayed'],
  stale: ['◌', C.gy, 'Stale'],
  offline: ['✕', C.rd, 'Offline'],
  reconnecting: ['↻', C.am, 'Reconnecting'],
};

export const BM: Record<string, StateToken> = {
  connected: ['⇄', C.gn, 'Connected'],
  disconnected: ['⊘', C.rd, 'Disconnected'],
  unknown: ['?', C.gy, 'Unknown'],
};

export const RM: Record<string, StateToken> = {
  normal: ['·', C.gn, 'Normal'],
  warning: ['▲', C.am, 'Warning'],
  critical: ['◆', C.rd, 'Critical'],
};

export const TM: Record<string, StateToken> = {
  flat: ['—', C.gy, 'Flat'],
  open: ['▸', C.bl, 'Open'],
  disabled: ['⊘', C.gy, 'Disabled'],
  inactive: ['☾', C.gy, 'Mkt closed'],
  sync: ['⟳', C.bl, 'Syncing'],
};

export const QM: Record<string, StateToken> = {
  current: ['✓', C.gn, 'Current'],
  partial: ['◪', C.am, 'Partial'],
  unknown: ['?', C.gy, 'Unknown'],
};

/** Alert severity glyph/color, keyed by the API severity enum. */
export const SEV: Record<string, StateToken> = {
  critical: ['◆', C.rd, 'Critical'],
  warning: ['▲', C.am, 'Warning'],
  info: ['i', C.bl, 'Info'],
};

/** Account-event glyph/color, keyed by the API event kind. */
export const EVENT_GLYPH: Record<string, readonly [string, string]> = {
  deposit: ['⇩', C.bl],
  withdrawal: ['⇧', C.bl],
  restart: ['⟳', C.am],
  disconnect: ['⊘', C.rd],
  reconnect: ['⇄', C.gn],
  gap: ['…', C.gy],
  close: ['▸', C.gn],
  note: ['·', C.gy],
};

/** Glyph/color for an account-event kind, with a definite fallback. */
export function eventGlyph(kind: string): readonly [string, string] {
  return EVENT_GLYPH[kind] ?? ['·', C.gy];
}
